// @react-compiler-disable
import { MapConfig, MapType, Position } from '../../../../domain/entities/map.types';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface MapState extends MapConfig {}

export const INITIAL_CENTER: Position = { lat: 40.7128, lng: -74.006 }; // Default to NYC if location access is denied
export const INITIAL_ZOOM = 5;
export const INITIAL_MAP_STATE: MapState = {
  center: INITIAL_CENTER,
  zoom: INITIAL_ZOOM,
  mapType: 'roadmap',
  gestureHandling: 'greedy',
  disableDefaultUI: true
};

const getUserLocation = (): Promise<Position> => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        reject(new Error(`Geolocation error: ${error.message}`));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

export const useMapState = (initialState: MapState = INITIAL_MAP_STATE) => {
  const [mapState, setMapState] = useState<MapState>(initialState);
  const defaultCenterRef = useRef<Position>(INITIAL_CENTER);

  useEffect(() => {
    const initializeMapWithUserLocation = async () => {
      try {
        const userLocation = await getUserLocation();
        defaultCenterRef.current = userLocation;
        setMapState((prev) => ({
          ...prev,
          center: userLocation,
          zoom: INITIAL_ZOOM
        }));
      } catch (error: unknown) {
        if (error instanceof Error && error.message.includes('Geolocation')) {
          // Silently fall back to default center for geolocation errors
          defaultCenterRef.current = INITIAL_CENTER;
          setMapState(INITIAL_MAP_STATE);
        } else {
          // Re-throw unexpected errors
          throw error;
        }
      }
    };

    initializeMapWithUserLocation();
  }, []);

  const handleZoomIn = useCallback(() => {
    setMapState((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom + 1, 20)
    }));
  }, []);

  const handleZoomOut = useCallback(() => {
    setMapState((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom - 1, 1)
    }));
  }, []);

  const handleMapTypeChange = useCallback((_: React.MouseEvent<HTMLElement>, newMapType: MapType | null) => {
    if (newMapType) {
      setMapState((prev) => ({ ...prev, mapType: newMapType }));
    }
  }, []);

  const handleReset = useCallback(() => {
    setMapState((prev) => ({
      ...prev,
      center: defaultCenterRef.current,
      zoom: INITIAL_ZOOM
    }));
  }, []);

  const handleZoomChanged = useCallback((zoom: number) => {
    setMapState((prev) => ({ ...prev, zoom }));
  }, []);

  const handleCenterChanged = useCallback((center: Position) => {
    setMapState((prev) => ({ ...prev, center }));
  }, []);

  return {
    mapState,
    handleZoomIn,
    handleZoomOut,
    handleMapTypeChange,
    handleReset,
    handleZoomChanged,
    handleCenterChanged
  };
};
